import React, { ReactNode } from 'react';

import { AutocompleteRenderInputParams, SxProps, Theme } from '@mui/material';
import {
  AutocompleteInput as AdminAutocompleteInput,
  AutocompleteInputProps as AdminAutocompleteInputProps,
  FieldTitle,
  InputHelperText,
  useChoicesContext,
  useInput,
} from 'react-admin';
import { TextField } from 'shared/mui/TextField/TextField';
import { fieldWidth } from 'themes/layout';

export type AutocompleteInputProps = {
  renderInput?: (params: AutocompleteRenderInputParams) => ReactNode;
  disabledHint?: string;
  readOnly?: boolean;
  shrink?: boolean;
} & AdminAutocompleteInputProps;

export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  sx,
  disabledHint,
  shrink = true,
  readOnly,
  ...props
}) => {
  const defaultSx: SxProps<Theme> = {
    '.MuiFormControl-root': {
      margin: 0,
    },
    ...fieldWidth.medium,
  };

  const {
    fieldState: fieldStateOverride,
    formState: formStateOverride,
    isRequired: isRequiredOverride,
    id: idOverride,
    field: fieldOverride,
    defaultValue,
    onBlur,
    onChange,
    parse,
    format,
    resource: resourceProp,
    source: sourceProp,
    choices: choicesProp,
    isFetching: isFetchingProp,
    isLoading: isLoadingProp,
    validate,
    size = 'small',
  } = props;

  const { resource, source } = useChoicesContext({
    choices: choicesProp,
    isFetching: isFetchingProp,
    isLoading: isLoadingProp,
    resource: resourceProp,
    source: sourceProp,
  });

  const {
    fieldState: { error, invalid, isTouched },
    formState: { isSubmitted },
  } = useInput({
    defaultValue,
    id: idOverride,
    field: fieldOverride,
    fieldState: fieldStateOverride,
    formState: formStateOverride,
    isRequired: isRequiredOverride,
    onBlur,
    onChange,
    parse,
    format,
    resource,
    source,
    validate,
    ...props,
  });

  return (
    <AdminAutocompleteInput
      readOnly={readOnly}
      renderInput={(params: AutocompleteRenderInputParams) => {
        const mergedTextFieldProps = {
          ...params.InputProps,
          ...props.TextFieldProps?.InputProps,
        };

        return (
          <TextField
            {...params}
            InputLabelProps={{ shrink: shrink || undefined }}
            // @ts-ignore
            InputProps={mergedTextFieldProps}
            error={(isTouched || isSubmitted) && invalid}
            helperText={<InputHelperText error={error?.message} helperText={props.helperText} />}
            label={
              <FieldTitle
                isRequired={(props as any)?.validate?.isRequired || isRequiredOverride}
                label={props.label}
                resource={resourceProp}
                source={source}
              />
            }
            placeholder={props?.disabled ? disabledHint : undefined}
            size={size}
            variant={props.variant}
          />
        );
      }}
      sx={{ ...defaultSx, ...sx } as any}
      {...props}
    />
  );
};
